import { useCallback, useEffect, useRef, useState } from 'react';
const DEFAULT_OPTIONS = {
    enableHighAccuracy: false,
    timeout: 5000,
    maximumAge: 1000 * 60 * 5
};
const DEFAULT_POLL_INTERVAL = 1000 * 60 * 1; // 1 minute
const DEFAULT_STALE_THRESHOLD = 1000 * 60 * 5; // 5 minutes
// Create a store outside the hook to share state between instances
const geolocationStore = {
    position: null,
    error: null,
    lastUpdateTime: 0,
    positionSubscribers: new Set(),
    errorSubscribers: new Set(),
    refreshingSubscribers: new Set(),
    staleSubscribers: new Set(),
    isRefreshing: false,
    refreshCallback: null,
    setPosition(pos) {
        this.position = pos;
        this.lastUpdateTime = pos ? Date.now() : 0;
        this.positionSubscribers.forEach((callback) => callback(pos));
    },
    setError(err) {
        this.error = err;
        this.errorSubscribers.forEach((callback) => callback(err));
    },
    setRefreshing(refreshing) {
        this.isRefreshing = refreshing;
        this.refreshingSubscribers.forEach((callback) => callback(refreshing));
    },
    setStale(stale) {
        this.staleSubscribers.forEach((callback) => callback(stale));
    },
    setRefreshCallback(callback) {
        this.refreshCallback = callback;
    }
};
// Custom error for stale position data
class StalePositionError {
    constructor() {
        this.code = 3; // Custom error code for stale position
        this.PERMISSION_DENIED = 1;
        this.POSITION_UNAVAILABLE = 2;
        this.TIMEOUT = 3;
        this.message = 'Position data is stale';
    }
}
function useGeolocation(options) {
    const [position, setPosition] = useState(geolocationStore.position);
    const [error, setError] = useState(geolocationStore.error);
    const [isRefreshing, setIsRefreshing] = useState(geolocationStore.isRefreshing);
    const [isStale, setIsStale] = useState(false);
    const pollIntervalRef = useRef(null);
    const staleCheckIntervalRef = useRef(null);
    // Function to check if position is stale
    const checkStalePosition = useCallback(() => {
        if (!geolocationStore.position) {
            geolocationStore.setStale(false);
            return;
        }
        const now = Date.now();
        const isPositionStale = now - geolocationStore.lastUpdateTime > DEFAULT_STALE_THRESHOLD;
        if (isPositionStale &&
            !geolocationStore.isRefreshing &&
            geolocationStore.refreshCallback) {
            // If position is stale and we're not already refreshing, trigger a refresh
            geolocationStore.refreshCallback().catch((err) => {
                console.error('Error refreshing stale position:', err);
            });
        }
        geolocationStore.setStale(isPositionStale);
    }, []);
    // Function to refresh the geolocation data
    const refresh = useCallback(() => {
        geolocationStore.setPosition(null);
        geolocationStore.setError(null);
        geolocationStore.setRefreshing(true);
        return new Promise((resolve, reject) => {
            if (options === null || options === void 0 ? void 0 : options.locationProvider) {
                options.locationProvider.getCurrentPosition((pos) => {
                    geolocationStore.setPosition(pos);
                    geolocationStore.setError(null);
                    geolocationStore.setRefreshing(false);
                    resolve(pos);
                }, (err) => {
                    geolocationStore.setPosition(null);
                    geolocationStore.setError(err);
                    geolocationStore.setRefreshing(false);
                    reject(err);
                }, DEFAULT_OPTIONS);
            }
            else {
                const error = new Error('Geolocation is not supported');
                geolocationStore.setError(error);
                reject(error);
            }
        });
    }, [options === null || options === void 0 ? void 0 : options.locationProvider]);
    useEffect(() => {
        // Add this component's setters to subscribers
        geolocationStore.positionSubscribers.add(setPosition);
        geolocationStore.errorSubscribers.add(setError);
        geolocationStore.refreshingSubscribers.add(setIsRefreshing);
        geolocationStore.staleSubscribers.add(setIsStale);
        geolocationStore.setRefreshCallback(refresh);
        // Initial position fetch
        refresh().catch((err) => {
            // The error is already set in state via geolocationStore.setError() in the refresh function
            // We just need to catch it to prevent uncaught promise rejection
            console.error('Error during initial geolocation fetch:', err);
        });
        // Set up polling interval only if watch is true
        if (options.watch) {
            pollIntervalRef.current = setInterval(() => {
                refresh().catch((err) => {
                    // The error is already set in state via geolocationStore.setError() in the refresh function
                    // We just need to catch it to prevent uncaught promise rejection
                    console.error('Error during geolocation polling:', err);
                });
            }, DEFAULT_POLL_INTERVAL);
        }
        // Set up stale check interval
        staleCheckIntervalRef.current = setInterval(() => {
            checkStalePosition();
        }, 1000); // Check every second
        return () => {
            // Remove this component's setters from subscribers
            geolocationStore.positionSubscribers.delete(setPosition);
            geolocationStore.errorSubscribers.delete(setError);
            geolocationStore.refreshingSubscribers.delete(setIsRefreshing);
            geolocationStore.staleSubscribers.delete(setIsStale);
            geolocationStore.setRefreshCallback(null);
            // Clear intervals
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
            if (staleCheckIntervalRef.current) {
                clearInterval(staleCheckIntervalRef.current);
            }
        };
    }, [options.locationProvider, options.watch, refresh, checkStalePosition]);
    return { position, error, isRefreshing, isStale, refresh };
}
export default useGeolocation;
